import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Axe, Pickaxe, Fish, Leaf, Hammer, Lock, CheckCircle2, TrendingUp, Timer, Target, RotateCcw } from "lucide-react";
import ClassSkills from "@/components/ClassSkills";

// Per-skill XP curve — MUST match backend `SKILL_XP_MULT_BY_SKILL` in
// /app/backend/server.py so the analytics chart doesn't diverge from
// the real level-up threshold. Gathering skills = 18×level, crafting +
// enchanting = 25×level.
const SKILL_XP_MULT = {
  woodcutting: 18,
  mining:      18,
  fishing:     18,
  gathering:   18,
  crafting:    25,
  enchanting:  25,
};

// Empirical "average XP granted per successful action" per skill. Used
// only for the "actions to next level" estimate — the actual game
// awards XP based on tier + roll but these medians match what players
// experience over hundreds of actions.
const AVG_XP_PER_ACTION = {
  woodcutting: 2,
  mining:      2,
  fishing:     3,
  gathering:   2,
  crafting:    10,
  enchanting:  15,
};

// §SESSION-XP: baseline (level, xp) snapshot per skill stored in
// sessionStorage, keyed by user id. Session lifetime = tab lifetime so
// closing the tab resets the tracker naturally.
const SESSION_KEY = "sessionSkillBaseline";

function loadSessionBaseline(userId) {
  try {
    const raw = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}");
    return raw[userId] || null;
  } catch { return null; }
}

function saveSessionBaseline(userId, baseline) {
  try {
    const raw = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "{}");
    raw[userId] = baseline;
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(raw));
  } catch { /* silent — session tracker is best-effort */ }
}

/** Total XP accumulated by a skill from level 1 to its current
 * (level, xp). Uses the same 18×/25× curve as the server so session
 * deltas are exact. */
function totalXpEarned(skill, level, xp) {
  const mult = SKILL_XP_MULT[skill] ?? 25;
  let sum = xp || 0;
  for (let lv = 1; lv < (level || 1); lv++) sum += lv * mult;
  return sum;
}

const META = {
  woodcutting: { Icon: Axe,     labelKey: "skills.woodcutting", bonus: "+2 HP / 2 lv", statKey: "max_hp",       statLabel: "HP" },
  mining:      { Icon: Pickaxe, labelKey: "skills.mining",      bonus: "+1 DEF / 3 lv", statKey: "defense",      statLabel: "DEF" },
  fishing:     { Icon: Fish,    labelKey: "skills.fishing",     bonus: "+1 DEX / 3 lv", statKey: "dexterity",    statLabel: "DEX" },
  gathering:   { Icon: Leaf,    labelKey: "skills.gathering",   bonus: "+1 INT / 3 lv", statKey: "intelligence", statLabel: "INT" },
  crafting:    { Icon: Hammer,  labelKey: "skills.crafting",    bonus: "+1 CHA / 3 lv", statKey: "charisma",     statLabel: "CHA" },
};

// Tiny inline bar chart: shows XP required for the current level and
// the next 4 levels so players can see the curve at a glance. Bars are
// scaled to the largest column (the last one).
function ProgressionChart({ skill, level }) {
  const mult = SKILL_XP_MULT[skill] ?? 25;
  const columns = Array.from({ length: 5 }, (_, i) => ({
    lv: level + i,
    xp: (level + i) * mult,
  }));
  const max = columns[columns.length - 1].xp;
  const avg = AVG_XP_PER_ACTION[skill] ?? 5;
  const actionsToNext = Math.ceil(columns[0].xp / avg);

  return (
    <div className="mt-2 pt-2 border-t border-[#161616]" data-testid={`skill-analytics-${skill}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <TrendingUp className="w-3 h-3 text-[#B89C3A]" strokeWidth={1.5} />
          <span className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c]">
            Curva progressione (×{mult})
          </span>
        </div>
        <span className="font-mono text-[9px] text-[#E5C34A]" data-testid={`skill-eta-${skill}`}>
          ~{actionsToNext} azioni al prossimo lv
        </span>
      </div>
      <div className="flex items-end gap-1 h-10">
        {columns.map((c, i) => {
          const h = Math.max(4, Math.round((c.xp / max) * 100));
          const isCurrent = i === 0;
          return (
            <div key={c.lv} className="flex-1 flex flex-col items-center gap-0.5"
                 title={`Lv ${c.lv} → ${c.lv + 1}: ${c.xp} xp`}>
              <div
                data-testid={`skill-bar-${skill}-${c.lv}`}
                className="w-full transition-all"
                style={{
                  height: `${h}%`,
                  background: isCurrent ? "#E5C34A" : "#4A5D23",
                  opacity: isCurrent ? 1 : 0.6,
                }}
              />
              <span className={`font-mono text-[8px] ${isCurrent ? "text-[#E5C34A]" : "text-[#666]"}`}>
                {c.lv}
              </span>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between font-mono text-[8px] text-[#666] mt-0.5 tracking-widest">
        <span>{columns[0].xp} xp</span>
        <span>{max} xp</span>
      </div>
    </div>
  );
}

export default function SkillsPanel({ ch }) {
  const { t } = useTranslation();
  const [perks, setPerks] = useState({});
  const skills = ch.skills || {};
  const bonuses = ch.skill_bonuses || {};

  // §SESSION-XP: capture the baseline at mount (once per user). Delta
  // vs current totals = XP earned this session.
  const userId = ch?.user_id || "anon";
  const [baseline, setBaseline] = useState(() => loadSessionBaseline(userId));
  useEffect(() => {
    if (!baseline) {
      const snap = {};
      for (const key of Object.keys(META)) {
        const s = skills[key] || { level: 1, xp: 0 };
        snap[key] = totalXpEarned(key, s.level, s.xp);
      }
      saveSessionBaseline(userId, snap);
      setBaseline(snap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => { api.skillPerks().then(setPerks); }, []);

  return (
    <div className="space-y-2">
      <ClassSkills ch={ch} />
      <div className="panel p-3">
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#8c8c8c]">{t("skills.title")}</div>
        <p className="italic text-[#666] text-xs mt-1">{t("skills.hint")}</p>
      </div>
      {Object.entries(META).map(([key, m]) => {
        const s = skills[key] || { level: 1, xp: 0 };
        const mult = SKILL_XP_MULT[key] ?? 25;
        const needed = s.level * mult;
        const pct = Math.min(100, (s.xp / needed) * 100);
        const Icon = m.Icon;
        const list = perks[key] || [];
        const contribution = bonuses[m.statKey] || 0;
        // Session delta (XP earned since baseline). 0 on first render
        // before the baseline is captured — hidden in that case.
        const currentTotal = totalXpEarned(key, s.level, s.xp);
        const sessionDelta = baseline ? Math.max(0, currentTotal - (baseline[key] || 0)) : 0;
        // Next locked perk on this skill's ladder — used for the
        // Perk Preview progress bar so goals feel concrete.
        const nextPerk = list.find(p => !p.unlocked);
        return (
          <div key={key} className="panel p-3" data-testid={`skill-${key}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-[#E55A00]" strokeWidth={1.5} />
                <span className="font-heading text-sm text-[#E2DFD8]">{t(m.labelKey)}</span>
              </div>
              <div className="flex items-center gap-2">
                {sessionDelta > 0 && (
                  <span
                    data-testid={`skill-session-delta-${key}`}
                    className="font-mono text-[9px] tracking-widest uppercase text-[#6E8B3D] border border-[#4A5D23] px-1.5 py-0.5 flex items-center gap-1"
                    title="XP guadagnata in questa sessione"
                  >
                    <Timer className="w-2.5 h-2.5" strokeWidth={2} />
                    +{sessionDelta} xp
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Reset baseline to the CURRENT total → future
                        // deltas are measured from now.
                        const snap = { ...(baseline || {}) };
                        snap[key] = currentTotal;
                        saveSessionBaseline(userId, snap);
                        setBaseline(snap);
                      }}
                      data-testid={`skill-session-reset-${key}`}
                      title="Ricomincia il tracker da qui"
                      className="ml-0.5 -mr-0.5 text-[#8c8c8c] hover:text-[#E33333] transition-colors"
                    >
                      <RotateCcw className="w-2.5 h-2.5" strokeWidth={2} />
                    </button>
                  </span>
                )}
                <span className="font-mono text-sm text-[#E5C34A]">Lv {s.level}</span>
              </div>
            </div>
            <div className="vbar">
              <span style={{ width: `${pct}%`, background: "#4A5D23" }} />
            </div>
            <div className="flex justify-between items-center mt-1">
              <div className="font-mono text-[10px] text-[#8c8c8c]">{s.xp}/{needed} xp</div>
              <div
                className="font-mono text-[10px] tracking-widest uppercase"
                style={{ color: contribution > 0 ? "#B89C3A" : "#555" }}
                data-testid={`skill-bonus-${key}`}
                title={m.bonus}
              >
                +{contribution} {m.statLabel} · {m.bonus}
              </div>
            </div>
            <ProgressionChart skill={key} level={s.level} />
            {nextPerk && (
              <NextPerkPreview
                skillKey={key}
                currentLevel={s.level}
                perk={nextPerk}
              />
            )}
            {list.length > 0 && (
              <div className="mt-2 space-y-1" data-testid={`perks-${key}`}>
                {list.map(p => (
                  <div
                    key={p.level}
                    data-testid={`perk-${key}-${p.level}`}
                    className={`flex items-center gap-2 p-1.5 border ${p.unlocked ? "border-[#4A5D23] bg-[#0a0d05]" : "border-[#262626]"}`}
                  >
                    {p.unlocked
                      ? <CheckCircle2 className="w-3 h-3 text-[#6E8B3D]" strokeWidth={2} />
                      : <Lock className="w-3 h-3 text-[#666]" strokeWidth={1.5} />}
                    <div className="flex-1 min-w-0">
                      <div className={`font-heading text-[11px] tracking-wider ${p.unlocked ? "text-[#E5C34A]" : "text-[#666]"}`}>
                        Lv{p.level} · {p.name}
                      </div>
                      <div className="text-[10px] italic text-[#8c8c8c]">{p.effect}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Next Perk Preview — a compact "goal card" showing the very next
// locked perk with a progress bar (current level → perk unlock level).
// Sits between the progression chart and the full perk ladder so
// players always have a concrete next milestone.
function NextPerkPreview({ skillKey, currentLevel, perk }) {
  const levelsRemaining = Math.max(0, perk.level - currentLevel);
  const pct = perk.level > 1
    ? Math.min(100, Math.max(0, (currentLevel / perk.level) * 100))
    : 0;
  return (
    <div
      className="mt-2 pt-2 border-t border-[#161616]"
      data-testid={`skill-next-perk-${skillKey}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <Target className="w-3 h-3 text-[#E5C34A]" strokeWidth={1.6} />
          <span className="font-mono text-[9px] tracking-widest uppercase text-[#8c8c8c]">
            Prossimo perk
          </span>
        </div>
        <span
          className="font-mono text-[9px] text-[#B89C3A]"
          data-testid={`skill-next-perk-remaining-${skillKey}`}
        >
          {levelsRemaining} lv rimanenti
        </span>
      </div>
      <div className="vbar">
        <span style={{ width: `${pct}%`, background: "#B89C3A" }} />
      </div>
      <div className="flex justify-between items-center mt-1 gap-2">
        <div className="min-w-0">
          <div className="font-heading text-[11px] tracking-wider text-[#E5C34A] truncate">
            Lv{perk.level} · {perk.name}
          </div>
          <div className="font-mono text-[9px] italic text-[#8c8c8c] truncate">{perk.effect}</div>
        </div>
        <span className="shrink-0 font-mono text-[9px] text-[#666] tracking-widest">
          {currentLevel}/{perk.level}
        </span>
      </div>
    </div>
  );
}
